import { z } from 'zod';
import { EvaluacionCumplimientoSchema } from '../utilidades/evaluacion-resistencia.js';
// =========================================================
// Schemas del módulo de Laboratorio de Concreto.
// =========================================================
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// =========================================================
// Categoría del concreto (afecta el análisis: curvas de resistencia
// y comportamiento de fragua difieren entre auto y convencional).
// =========================================================
export const CATEGORIAS_CONCRETO = ['convencional', 'autocompactable', 'otro'];
export const CategoriaConcretoSchema = z.enum(CATEGORIAS_CONCRETO);
// =========================================================
// Curva teórica de resistencia (ASTM C-150 Type I).
// pct_resistencia es fracción decimal: 0.65 = 65% del F'C a los 7 días.
// Resistencia esperada (kg/cm²) = F'C × pct_resistencia.
// =========================================================
export const PuntoCurvaTeoricaSchema = z.object({
    edad_dias: z.number().int().positive(),
    pct_resistencia: z.number(),
    descripcion: z.string().nullable(),
});
// =========================================================
// Actividades (catálogo gestionable)
// =========================================================
export const ActividadSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(100),
    activo: z.boolean(),
    orden: z.number().int(),
});
export const CrearActividadRequestSchema = z.object({
    nombre: z.string().trim().min(1).max(100),
    orden: z.number().int().default(0),
});
export const ActualizarActividadRequestSchema = z.object({
    nombre: z.string().trim().min(1).max(100).optional(),
    orden: z.number().int().optional(),
    activo: z.boolean().optional(),
});
// =========================================================
// Fotos de muestra (almacenadas en Azure Blob Storage)
// =========================================================
export const FotoMuestraSchema = z.object({
    id: z.number().int().positive(),
    id_muestra: z.number().int().positive(),
    /** Ensayo al que está asignada la foto (NULL = foto general de la muestra). */
    id_ensayo: z.number().int().positive().nullable(),
    nombre_original: z.string().nullable(),
    content_type: z.string(),
    tamano_bytes: z.number().int().nullable(),
    creado_en: z.string(),
    /** URL temporal (SAS de lectura) para mostrar la imagen. Se regenera en cada listado. */
    url: z.string(),
});
export const ListaFotosMuestraResponseSchema = z.object({
    fotos: z.array(FotoMuestraSchema),
});
/** Body de PATCH /api/lab/fotos/{id} — solo reasigna a un ensayo o lo desliga. */
export const ActualizarFotoRequestSchema = z.object({
    id_ensayo: z.number().int().positive().nullable(),
});
// =========================================================
// Mediciones (probetas individuales de un ensayo)
// =========================================================
export const MedicionSchema = z.object({
    id: z.number().int().positive(),
    id_ensayo: z.number().int().positive(),
    resistencia_mpa: z.number(),
    orden: z.number().int(),
    notas: z.string().nullable(),
});
export const CrearMedicionRequestSchema = z.object({
    resistencia_mpa: z.number().positive().max(200),
    orden: z.number().int().positive().default(1),
    notas: z.string().trim().max(500).optional().nullable(),
});
export const ActualizarMedicionRequestSchema = z.object({
    resistencia_mpa: z.number().positive().max(200).optional(),
    orden: z.number().int().positive().optional(),
    notas: z.string().trim().max(500).optional().nullable(),
});
// =========================================================
// Ensayos (1 por edad evaluada de una muestra)
// =========================================================
/**
 * Resumen calculado del ensayo (incluye agregado de mediciones).
 * Lo arma el backend a partir de `lab.v_ensayos_resumen` + las
 * mediciones individuales para el detalle.
 */
export const EnsayoSchema = z.object({
    id: z.number().int().positive(),
    id_muestra: z.number().int().positive(),
    edad_dias: z.number().int().positive(),
    /** Fecha en que se falló realmente el cilindro (puede diferir de la programada). */
    fecha_prueba: z.string().nullable(),
    /**
     * Fecha teórica programada (fecha_colado + edad_dias). Referencia para
     * detectar si la prueba se hizo en otra fecha.
     */
    fecha_prueba_programada: z.string().nullable(),
    /** true si fecha_prueba difiere de la programada (se ajustó a mano). */
    fecha_ajustada: z.boolean(),
    /** Email de quién ajustó la fecha (null si nunca se ajustó). */
    fecha_ajustada_por_email: z.string().nullable(),
    /** Cuándo se ajustó (ISO). */
    fecha_ajustada_en: z.string().nullable(),
    /** Motivo opcional del ajuste de fecha. */
    fecha_ajustada_motivo: z.string().nullable(),
    notas: z.string().nullable(),
    /** Cantidad de mediciones (probetas) registradas. */
    cantidad_mediciones: z.number().int(),
    /** Promedio de MPa. Null si no hay mediciones aún. */
    resistencia_mpa_promedio: z.number().nullable(),
    /** Promedio en kg/cm² (factor 10.197). */
    resistencia_kg_cm2_promedio: z.number().nullable(),
    /** Mediciones individuales (sólo se llena en el endpoint detalle). */
    mediciones: z.array(MedicionSchema).optional(),
});
export const CrearEnsayoRequestSchema = z.object({
    edad_dias: z.number().int().positive().max(365),
    fecha_prueba: z.string().regex(ISO_DATE).optional().nullable(),
    notas: z.string().trim().max(2000).optional().nullable(),
});
export const ActualizarEnsayoRequestSchema = z.object({
    fecha_prueba: z.string().regex(ISO_DATE).optional().nullable(),
    /**
     * Motivo opcional del cambio de fecha. Solo tiene sentido cuando se manda
     * `fecha_prueba`. Queda registrado junto con quién/cuándo cambió.
     */
    fecha_ajustada_motivo: z.string().trim().max(500).optional().nullable(),
    notas: z.string().trim().max(2000).optional().nullable(),
});
// =========================================================
// Muestras
// =========================================================
export const MuestraListadoItemSchema = z.object({
    id: z.number().int().positive(),
    numero_muestra: z.number().int().positive(),
    obra_works_no: z.string().nullable(),
    obra_display_name: z.string().nullable(),
    id_casa: z.string().nullable(),
    /** Planta de la que salió el concreto (Bianca E50 / Roja E50 / Fiori …). */
    planta_nombre: z.string().nullable(),
    actividad_nombre: z.string(),
    fecha_colado: z.string(),
    proveedor: z.string(),
    fc_objetivo: z.number().int(),
    /** convencional / autocompactable / otro / null. */
    categoria_concreto: CategoriaConcretoSchema.nullable(),
    tipo_concreto_display: z.string(),
    /**
     * Cumplimiento agregado: peor caso de sus ensayos contra la curva
     * teórica × F'C. cumple|marginal|incumple|sin_dato. Calculado por el
     * backend para evitar que cada cliente repita el cómputo.
     */
    evaluacion_cumplimiento: EvaluacionCumplimientoSchema,
    /** Cantidad de ensayos registrados (de cualquier edad). */
    cantidad_ensayos: z.number().int(),
    /** Edades de ensayos ya completados (con al menos 1 medición). */
    edades_completadas: z.array(z.number().int()),
    id_colada: z.number().int().nullable(),
    codigo_interno_colada: z.number().int().nullable(),
});
export const MuestraDetalleSchema = z.object({
    id: z.number().int().positive(),
    numero_muestra: z.number().int().positive(),
    obra_works_no: z.string().nullable(),
    obra_display_name: z.string().nullable(),
    id_casa: z.string().nullable(),
    /** Planta de la que salió el concreto (Bianca E50 / Roja E50 / Fiori …). */
    planta_nombre: z.string().nullable(),
    id_actividad: z.number().int().positive(),
    actividad_nombre: z.string(),
    fecha_colado: z.string(),
    proveedor: z.string(),
    id_colada: z.number().int().nullable(),
    codigo_interno_colada: z.number().int().nullable(),
    id_receta_bc: z.number().int().nullable(),
    receta_bc_codigo: z.string().nullable(),
    receta_bc_descripcion: z.string().nullable(),
    fc_objetivo: z.number().int(),
    /** convencional / autocompactable / otro / null. */
    categoria_concreto: CategoriaConcretoSchema.nullable(),
    tipo_concreto_libre: z.string().nullable(),
    /** Texto final mostrado al usuario: receta BC si existe, sino texto libre. */
    tipo_concreto_display: z.string(),
    notas: z.string().nullable(),
    creado_por_email: z.string().nullable(),
    creado_en: z.string(),
    actualizado_en: z.string(),
    ensayos: z.array(EnsayoSchema),
});
/**
 * Prefijos de proyecto para el filtro rápido del laboratorio. Son la parte
 * inicial del works_no / casa (ej. "VN-M.12" → VN, "VB-6.24" → VB).
 */
export const PROYECTOS_LAB = ['VN', 'VC', 'VI', 'VB'];
// Filtros del listado
export const ListarMuestrasRequestSchema = z.object({
    obra_works_no: z.string().optional(),
    /** Prefijo de proyecto (VN/VC/VI/VB). Matchea inicio de obra o casa. */
    proyecto: z.string().min(1).max(10).optional(),
    id_actividad: z.coerce.number().int().positive().optional(),
    fc_objetivo: z.coerce.number().int().positive().optional(),
    desde: z.string().regex(ISO_DATE).optional(),
    hasta: z.string().regex(ISO_DATE).optional(),
    q: z.string().min(1).max(100).optional(),
    /**
     * Filtro por estado de cumplimiento de resistencia. Se evalúa contra la
     * curva teórica en el backend. Como la evaluación no es una columna SQL,
     * cuando viene este filtro el backend pagina en memoria (volumen de lab
     * es bajo, cientos-miles de muestras).
     */
    cumplimiento: EvaluacionCumplimientoSchema.optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    por_pagina: z.coerce.number().int().min(1).max(200).default(50),
});
export const ListaMuestrasResponseSchema = z.object({
    muestras: z.array(MuestraListadoItemSchema),
    total: z.number().int(),
    pagina: z.number().int(),
    por_pagina: z.number().int(),
});
// Crear muestra. Opcionalmente pre-crea los ensayos a las edades indicadas
// (default 7/14/28) — así el detalle queda listo para que el laboratorista
// solo entre los MPa cuando cada cilindro falle, sin tener que "agregar
// ensayo" manualmente cada vez.
/**
 * Plantas conocidas para el dropdown de la muestra. Las Blend (Bianca/Roja)
 * se derivan automáticamente de la colada; las Fiori se eligen a mano porque
 * ese concreto no pasa por las coladas Blend. `Otra` deja el campo libre.
 */
export const PLANTAS_LAB = ['Bianca E50', 'Roja E50', 'FIORI DB 460', 'FIORI DB 180'];
export const CrearMuestraRequestSchema = z.object({
    obra_works_no: z.string().trim().min(1).max(20).optional().nullable(),
    id_casa: z.string().trim().max(50).optional().nullable(),
    /** Planta del concreto. Texto libre (dropdown sugerido en la UI). */
    planta_nombre: z.string().trim().max(50).optional().nullable(),
    id_actividad: z.number().int().positive(),
    fecha_colado: z.string().regex(ISO_DATE),
    proveedor: z.string().trim().min(1).max(100).default('ADELANTE DESARROLLOS'),
    id_colada: z.number().int().positive().optional().nullable(),
    id_receta_bc: z.number().int().positive().optional().nullable(),
    fc_objetivo: z.number().int().positive().max(2000),
    /** Categoría del concreto (afecta análisis de curvas). Opcional. */
    categoria_concreto: CategoriaConcretoSchema.optional().nullable(),
    tipo_concreto_libre: z.string().trim().max(100).optional().nullable(),
    notas: z.string().trim().max(2000).optional().nullable(),
    /**
     * Edades a pre-crear como ensayos vacíos. Default [7, 14, 28]: las edades
     * típicas. Pasar [] (array vacío) para crear muestra sin ensayos.
     * El backend deduplica y filtra valores fuera de rango (1-365).
     */
    edades_ensayos: z.array(z.number().int().positive().max(365)).optional().default([7, 14, 28]),
});
// Editar muestra (todos los campos opcionales menos el id implícito en URL)
export const ActualizarMuestraRequestSchema = CrearMuestraRequestSchema.partial();
// =========================================================
// Importación masiva desde Excel histórico
// =========================================================
export const ErrorImportLabSchema = z.object({
    fila_excel: z.number().int(),
    numero_muestra: z.string().nullable(),
    mensaje: z.string(),
});
export const ImportarExcelLabResponseSchema = z.object({
    total_filas: z.number().int(),
    /** Muestras nuevas creadas (no existían en BD). */
    muestras_insertadas: z.number().int(),
    /**
     * Muestras existentes a las que se les agregó al menos un ensayo/medición
     * o se les completó una fecha/actividad faltante.
     */
    muestras_actualizadas: z.number().int(),
    /**
     * Muestras que matchearon una fila del Excel pero no requirieron cambios
     * (ya tenían todo lo que el Excel trae). Antes se llamaba "duplicadas".
     */
    muestras_duplicadas: z.number().int(),
    ensayos_insertados: z.number().int(),
    /** Ensayos existentes vacíos cuya fecha_prueba se actualizó desde el Excel. */
    ensayos_actualizados: z.number().int(),
    mediciones_insertadas: z.number().int(),
    actividades_creadas: z.number().int(),
    /**
     * Avisos no bloqueantes: typos de año corregidos, filas sin match que se
     * crearon como muestra nueva, etc. Para que el laboratorio los revise.
     */
    advertencias: z.array(z.string()),
    errores: z.array(ErrorImportLabSchema),
});
//# sourceMappingURL=laboratorio.js.map